import { all, equals, path, values } from 'ramda'
import { useEffect, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router'
import { isNullOrEmpty } from '@/utils'
import { Button } from '@/components/button'
import { ButtonBar } from '@/components/button-bar'
import Content from '@/components/content'
import ContentWrapper from '@/components/content-wrapper'
import { confirm } from '@/components/dialog'
import { WhitePage } from '@/components/page'
import PageTitle from '@/components/page-title'
import { toast } from '@/components/toast-notifications'
import { NamedWizard, WizardContent } from '@/components/wizard'
import useRole from '@/hooks/use-role'
import { useFlag } from '@/flags'
import useGetPadInfo from '@/hooks/use-get-pad-info'
import useCurrentSession from '@/hooks/use-current-session'
import useBetaUser from '@/hooks/use-beta-user'
import { select } from '@/store'
import scrollTop from '@/utils/scroll-top'
import { Box } from '@/components/structure'
import { structure } from '@/styles'
import Context from './context'
import ReviewTerms from '../../review-terms'
import ReviewSignatures from '../../review-signatures'
import ReviewDetails from '../../review-details'

const WIZARD_ID = 'sign-wizard'

const VALID_STEPS = ['review', 'terms', 'sign']

const getPreviousStep = step => {
  const currentStepIndex = VALID_STEPS.indexOf(step)
  return currentStepIndex === 0 ? null : VALID_STEPS[currentStepIndex - 1]
}

const validateStep = step => (VALID_STEPS.indexOf(step) !== -1 ? step : 'review')

const Sign = ({ contract, user, sticky, noTitle }) => {
  const [agreePad, setAgreePad] = useState(false)
  const [authPad, setAuthPad] = useState(false)
  const hasAgreedPad = agreePad && authPad
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const session = useCurrentSession()
  const betaUser = useBetaUser()
  const betaUserPad = betaUser.pad
  const currentBetaUser = betaUserPad.includes(parseInt(session.id))
  const showPad = useFlag(['features', 'pad'], false) && currentBetaUser
  const resetScroll = () => scrollTop(WIZARD_ID)
  const { step = 'review', listingId, contractId } = useParams()
  const history = useHistory()
  const role = useRole()
  const isTenant = role === 'tenant'
  const isBillingUser =
    isTenant && (isNullOrEmpty(contract.billing_user_id) || user.id === contract.billing_user_id)

  const [checkboxes, setCheckboxes] = useState({})
  const checkboxesComplete = step !== 'review' || all(equals(true), values(checkboxes))

  const { data } = useGetPadInfo({ enabled: showPad && isBillingUser })
  const hasPadInfo = !isNullOrEmpty(data)
  const [tenantPaymentMethodSelection, setTenantPaymentMethodSelection] = useState(
    contract.pad_requested ? 'pad' : 'other'
  )
  const invalidPad = (!hasPadInfo || !hasAgreedPad) && tenantPaymentMethodSelection === 'pad'

  const currentStep = validateStep(step)
  const previousStep = getPreviousStep(currentStep)
  const isPreviousStepDone = useSelector(state =>
    select.contract.isStepComplete(state, {
      step: previousStep
    })
  )
  const stepStatus = useSelector(state =>
    select.contract.isStepComplete(state, {
      step: currentStep
    })
  )
  const signature = useSelector(path(['contract', 'signature']))

  useEffect(() => {
    resetScroll()
  }, [step, listingId, contractId])

  useEffect(() => {
    return () => dispatch.contract.clearSteps()
  }, [dispatch.contract])

  const goToStep = useCallback(
    newStep => {
      if (isTenant) {
        history.push(
          `/rental/${listingId || contract?.listingId}/contract/${contractId}/${newStep}`
        )
      } else {
        history.push(
          `/listing/${listingId || contract?.listingId}/contract/${contractId}/${newStep}`
        )
      }
    },
    [contract?.listingId, contractId, history, listingId, isTenant]
  )
  useEffect(() => {
    if (previousStep && !isPreviousStepDone) goToStep('review')
  }, [goToStep, isPreviousStepDone, previousStep])

  const goToNextStep = () => {
    const steps = VALID_STEPS
    const currentIndex = steps.indexOf(step)

    if (currentIndex !== steps.length - 1) {
      goToStep(steps[currentIndex + 1])
    }
  }

  const isRenewal = contract?.listing?.state_machine === 'rented'
  const dashboardUrl = isRenewal
    ? `/tenant/${contract?.listingId}`
    : `/listing/${contract?.listingId}`

  const handleBack = async () => {
    if (step === 'review' && isTenant) {
      history.replace(`/rental/${contract?.listingId}`)
      resetScroll()
    } else if (step === 'review') {
      return confirm(
        t('b.contract.edit.cancel_contract_confirmation.title', 'Cancel Contract'),
        t(
          'b.contract.edit.cancel_contract_confirmation.text',
          'Are you sure you want to cancel this contract?'
        )
      ).then(async () => {
        const { response } = await dispatch.contract.cancelContract({
          contractId: contract?.id,
          listingId: contract?.listingId
        })

        if (response.ok) {
          history.replace(dashboardUrl)
          toast(t('b.contract.view.contract_cancelled.text', 'The contract has been cancelled.'), {
            title: t('b.contract.view.contract_cancelled.title', 'Contract Cancelled'),
            type: 'success'
          })
        }
      })
    } else {
      isTenant
        ? history.replace(`/rental/${contract?.listingId}/contract/${contract?.id}/review`)
        : history.replace(`/listing/${contract?.listingId}/contract/${contract?.id}/review`)
      resetScroll()
    }
  }

  const handleContinue = async () => {
    if (step !== 'sign') {
      if (step === 'review') {
        if (showPad && isBillingUser && tenantPaymentMethodSelection === 'pad' && !hasPadInfo) {
          return toast(
            t(
              'b.contract.view.connect_your_pad_account.text',
              'Before you can finalize the contract, please either connect your bank account to use PAD or select Other Payment Method.'
            ),
            {
              title: t(
                'b.contract.view.connect_your_pad_account.title',
                'Connect Your Bank Account'
              ),
              type: 'error'
            }
          )
        }
      }
      goToNextStep()
    } else {
      if (showPad) {
        const paymentMethod = isBillingUser ? tenantPaymentMethodSelection : undefined
        await dispatch.contract.signContract({
          contractId: contract.id,
          paymentMethod
        })
      } else {
        await dispatch.contract.signContractWithoutPad({
          contractId: contract.id
        })
      }
      await dispatch.toaster.fetch()
      isTenant
        ? history.push(`/rental/${contract?.listingId}/contract/${contract.id}`)
        : history.push(`/listing/${contract?.listingId}/contract/${contract.id}`)
    }
  }

  const allowedToCancel = () => {
    const operationType = user?.operation_type
    return (step === 'review' && operationType === 'landlord') || step !== 'review'
  }

  const WIZARD_STEPS = {
    review: t('b.contract.view.sign_step_review_details', 'Review Details'),
    terms: t('b.contract.view.sign_step_read_terms', 'Read Terms'),
    sign: t('b.contract.view.sign_step_sign.text', 'Sign')
  }

  if (!contract.id) return null

  return (
    <WhitePage sticky={sticky} hideOverflow>
      {!noTitle && <PageTitle>{t('b.contract.view.contract.title', 'Contract')}</PageTitle>}
      <NamedWizard
        steps={WIZARD_STEPS}
        currentStep={step}
        stickyTop={structure.header.height}
        stickyTopMobile={structure.header.heightMobile}
      />
      <ContentWrapper className="mb-50">
        <Content id={WIZARD_ID} top={50} bottom={40}>
          <Context.Provider
            value={{
              checkboxes,
              setCheckboxes,
              tenantPaymentMethodSelection,
              setTenantPaymentMethodSelection,
              agreePad,
              setAgreePad,
              authPad,
              setAuthPad
            }}
          >
            <WizardContent currentStep={step}>
              <ReviewDetails
                index="review"
                contract={contract}
                userType={user.operation_type}
                isBillingUser={isBillingUser}
              />
              <ReviewTerms index="terms" />
              <ReviewSignatures
                index="sign"
                contract={contract}
                userType={user.operation_type}
                currentUser={user.id}
              />
            </WizardContent>
          </Context.Provider>
        </Content>
      </ContentWrapper>
      <ButtonBar sticky>
        {allowedToCancel() && (
          <Button
            theme={step === 'review' ? 'delete' : 'secondary'}
            size="small"
            onClick={handleBack}
          >
            {step === 'review'
              ? t('b.contract.view.cancel_contract.button', 'Cancel Contract')
              : t('common.back', 'Back')}
          </Button>
        )}
        <Box left="auto">
          <Button
            theme="primary"
            size="small"
            type="submit"
            disabled={
              step !== 'sign'
                ? !stepStatus ||
                  !checkboxesComplete ||
                  (step === 'review' && invalidPad && isTenant && isBillingUser && showPad)
                : signature === null
            }
            onClick={handleContinue}
          >
            {step !== 'sign'
              ? t('b.contract.view.continue.button', 'Continue')
              : t('b.contract.view.complete_signing.button', 'Complete Signing')}
          </Button>
        </Box>
      </ButtonBar>
    </WhitePage>
  )
}

export default Sign
